import { Component, app, component } from "modular-openscriptjs";
import { either, uniqueId, reflow, parseCSSDuration } from "../../helpers";
import { dom } from "../../fotastart";

const h = app("h");

export default class Slider extends Component {
	render(config, ...args) {
		const hook = `slider-${config?.uid ?? uniqueId()}`;
		this.hook = hook;

		const slides = config?.slides ?? [];
		if (
			slides.length > 0 &&
			!slides.some((el) => el.classList.contains("active"))
		) {
			slides[0].classList.add("active");
		}

		slides.forEach((slide, index) => {
			slide.setAttribute("data-slide", hook);
		});

		const vertical = config?.vertical ?? false;
		const cycle = config?.cycle ?? false; // Autoplay?
		this.autoplay = cycle;
		this.interval = config?.interval ?? 5000;

		const hasControls = config?.hasControls ?? true; // Prev|Next Buttons
		const hasIndicators = config?.hasIndicators ?? true;
		const currentIndex = slides.findIndex((el) =>
			el.classList.contains("active"),
		);

		return h.div(
			{
				data_slider: hook,
				class: ["slider", vertical ? "slider-vertical" : ""]
					.join(" ")
					.trim(),
			},
			h.div(
				{
					data_slider_stack: hook,
					class: "slider-stack",
				},
				either(hasControls, () => [
					h.button(
						{
							data_slider_prev: hook,
							class: "slider-control-prev",
							title: "Previous",
							onclick: this.method("prev", [hook]),
						},
						"<",
					),
					h.button(
						{
							data_slider_next: hook,
							class: "slider-control-next",
							title: "Next",
							onclick: this.method("next", [hook]),
						},
						">",
					),
				]),
				either(cycle, () => [
					h.button({
						data_slider_cycle: hook,
						class: "slider-control-cycle active",
						title: "Autoplay/Pause",
						onclick: this.method("toggleCycle", [hook]),
					}),
				]),
				slides,
			),
			either(hasIndicators, () => {
				var indicators = [];
				for (var index = 0; index < slides.length; index++) {
					indicators.push(
						h.button({
							data_slider_indicator: hook,
							class: [
								"slider-indicator",
								index === currentIndex ? "active" : "",
							]
								.join(" ")
								.trim(),
							onclick: this.method("to", [hook, index]),
						}),
					);
				}
				return h.div(
					{
						data_slider_indicators: hook,
						class: "slider-indicators",
					},
					indicators,
				);
			}),
			...args,
		);
	}

	$_mounted(componentId) {
		let c = component(componentId);
		if (c && c.autoplay) {
			c.cycle(c.hook, c.interval);
		}
	}

	$_rerendered_rendered(componentId) {
		let c = component(componentId);
		if (c && c.autoplay) {
			c.cycle(c.hook, c.interval);
		}
	}

	cleanUp() {
		super.cleanUp();

		clearInterval(this.intervalId);
	}

	// DOM Helper Methods
	// ==================

	__slider__(hook) {
		return dom.get(`[data-slider="${hook}"]`);
	}
	__slides__(hook) {
		return dom.all(`[data-slide="${hook}"]`);
	}
	__currentSlide__(hook) {
		return dom.get(`.active[data-slide="${hook}"]`);
	}
	__cycleControl__(hook) {
		return dom.get(`[data-slider-cycle="${hook}"]`);
	}
	__indicators__(hook) {
		return dom.all(`[data-slider-indicator="${hook}"]`);
	}

	// Slider Control Methods
	// ======================

	next(hook) {
		const slides = this.__slides__(hook);
		const currentSlide = this.__currentSlide__(hook);
		
		const current = [...slides].indexOf(currentSlide);
		const target = (current + 1) % slides.length;
		
		this.to(hook, target, "next");
	}

	prev(hook) {
		const slides = this.__slides__(hook);
		const currentSlide = this.__currentSlide__(hook);
		const current = [...slides].indexOf(currentSlide);
		const target = (current - 1 + slides.length) % slides.length;
		this.to(hook, target, "prev");
	}

	cycle(hook, interval = 5000, start = false) {
		const slider = this.__slider__(hook);
		if (slider?.hasAttribute("data-slider-cycle-id")) return;

		if (start) this.next(hook);
		clearInterval(this.intervalId);
		const id = setInterval(() => {
			if (!this.mounted) {
				clearInterval(this.intervalId);
				return;
			}
			this.next(hook);
		}, interval); // number
		slider?.setAttribute("data-slider-cycle-id", id);
		this.intervalId = id;
		return id;
	}

	pause(hook) {
		const slider = this.__slider__(hook);
		const id = slider?.getAttribute("data-slider-cycle-id");
		if (!id) return;

		clearInterval(id);
		slider.removeAttribute("data-slider-cycle-id");
	}

	toggleCycle(hook) {
		const slider = this.__slider__(hook);
		const control = this.__cycleControl__(hook);
		if (slider?.hasAttribute("data-slider-cycle-id")) {
			this.pause(hook);
			control?.classList.remove("active");
		} else {
			this.cycle(hook);
			control?.classList.add("active");
		}
	}

	/**
	 * Slide to given target index
	 * @param {String} hook Hook of the slider
	 * @param {Number} target Index of the target slide
	 * @param {String?} direction Either "next" or "prev"
	 * @returns {void}
	 */
	to(hook, target, direction = null) {
		if (direction && !["prev", "next"].includes(direction)) return;
		if (target < 0) return;

		const slides = this.__slides__(hook);
		const currentSlide = this.__currentSlide__(hook);
		const current = [...slides].indexOf(currentSlide);

		if (target > slides.length - 1) return;
		if (target == current) return;

		if (!direction) {
			direction = target > current ? "next" : "prev";
		}

		const targetSlide = slides[target];

		if (!targetSlide) return;

		const toNext = direction == "next";

		// Move target slide to needed position before the animation
		targetSlide?.classList.add(toNext ? "is-next" : "is-prev");
		reflow(targetSlide);

		// Start transition
		// Fire any event here???
		targetSlide?.classList.remove("is-prev", "is-next");
		targetSlide?.classList.add("active");
		currentSlide?.classList.remove("active");
		currentSlide?.classList.add(toNext ? "is-prev" : "is-next");

		// Highlight correct indicator
		const indicators = this.__indicators__(hook);
		if (indicators) {
			indicators[current]?.classList.remove("active");
			indicators[target]?.classList.add("active");
		}

		// After transition, clean up
		const timeout = parseCSSDuration(
			window.getComputedStyle(currentSlide).transitionDuration,
		);
		setTimeout(() => {
			currentSlide?.classList.remove("is-prev", "is-next");
			// Fire any event here???
		}, timeout);
	}
}
